/**
 * Cards or table — a preference, not part of the search.
 *
 * Deliberately NOT in the URL, unlike every filter. A shared link should reproduce the SEARCH, and
 * carrying the sender's view preference into the recipient's browser overrides a choice they
 * already made for every other search they run. The filters answer "which postings"; this answers
 * "how do I like to read them", and only the first belongs in a link.
 *
 * Every access is wrapped. `localStorage` is not merely empty in a private window or with site data
 * blocked — the getter itself THROWS, and an unhandled throw here happens during the first render
 * of the results screen, which would blank the whole page for exactly the users least likely to
 * report why.
 */

export const VIEW_MODES = ['cards', 'table'] as const;
export type ViewMode = (typeof VIEW_MODES)[number];

export const DEFAULT_VIEW: ViewMode = 'cards';

/** Namespaced: several huaca modules share an origin in local dev. */
const KEY = 'huaca.tenders.view';

const isViewMode = (value: unknown): value is ViewMode => VIEW_MODES.includes(value as ViewMode);

/** The stored preference, or the default — never a throw, and never a value outside the union. */
export function readViewMode(): ViewMode {
  try {
    const stored = window.localStorage.getItem(KEY);
    // A hand-edited or stale value is dropped rather than rendered: `view === 'table'` would be
    // false for it and the screen would silently fall through to cards while the switch showed
    // neither button pressed.
    return isViewMode(stored) ? stored : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
}

/** Best-effort. A browser that refuses to store it still renders the choice for this session. */
export function writeViewMode(mode: ViewMode): void {
  try {
    window.localStorage.setItem(KEY, mode);
  } catch {
    /* private window, blocked site data, or a full quota — none of which should break the view */
  }
}
